import {
  Dispatch,
  SetStateAction,
  useLayoutEffect,
  useRef,
} from 'react';

interface ViewportSnapshot {
  scroller: HTMLElement;
  marker: HTMLElement | null;
  scrollTop: number;
  disclosureTop: number;
  disclosureHeight: number;
}

/**
 * Keeps the disclosure header at the same viewport coordinate while its body
 * changes height. Growing the trailing chat spacer before paint prevents the
 * browser from clamping scrollTop when a tall expanded body is collapsed.
 */
export const useStableDisclosure = (
  expanded: boolean,
  setExpanded: Dispatch<SetStateAction<boolean>>,
) => {
  const disclosureRef = useRef<HTMLDivElement>(null);
  const snapshotRef = useRef<ViewportSnapshot | null>(null);

  const toggleDisclosure = () => {
    const disclosure = disclosureRef.current;
    const scroller = disclosure?.closest<HTMLElement>('[data-testid="chat-scroll-container"]');
    if (disclosure && scroller) {
      snapshotRef.current = {
        scroller,
        marker: scroller.querySelector<HTMLElement>('[data-chat-end-marker]'),
        scrollTop: scroller.scrollTop,
        disclosureTop: disclosure.getBoundingClientRect().top,
        disclosureHeight: disclosure.getBoundingClientRect().height,
      };
    }
    setExpanded((value) => !value);
  };

  useLayoutEffect(() => {
    const disclosure = disclosureRef.current;
    const snapshot = snapshotRef.current;
    if (disclosure) disclosure.dataset.chatDisclosureExpanded = String(expanded);
    if (!disclosure || !snapshot || !snapshot.scroller.isConnected) return;
    const heightDelta = snapshot.disclosureHeight - disclosure.getBoundingClientRect().height;
    if (snapshot.marker) {
      if (heightDelta > 0.5) {
        snapshot.marker.style.height = `${Math.max(0, snapshot.marker.offsetHeight + heightDelta)}px`;
      } else if (heightDelta < -0.5) {
        // The submitted-turn anchor may have reduced the marker to zero. When
        // the disclosure grows, restore enough trailing space for its final
        // lines to scroll above the fixed composer.
        const composer = snapshot.scroller
          .querySelector<HTMLElement>('#input-text-field')
          ?.closest<HTMLElement>('form');
        if (composer) {
          const scrollerBottom = snapshot.scroller.getBoundingClientRect().bottom;
          const minimumHeight = Math.ceil(
            Math.max(0, scrollerBottom - composer.getBoundingClientRect().top + 12),
          );
          if (snapshot.marker.offsetHeight < minimumHeight) {
            snapshot.marker.style.height = `${minimumHeight}px`;
          }
        }
      }
    }
    snapshot.scroller.scrollTop = snapshot.scrollTop;
    const topDelta = disclosure.getBoundingClientRect().top - snapshot.disclosureTop;
    if (Math.abs(topDelta) > 0.5) {
      snapshot.scroller.scrollTop += topDelta;
    }
    snapshotRef.current = null;
  }, [expanded]);

  return { disclosureRef, toggleDisclosure };
};
